import {logger} from '../../../logger';
import React from 'react';
import {createPortal} from 'react-dom';
import {observable, action, makeObservable, runInAction} from 'mobx';
import {observer} from 'mobx-react';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {add_to_cart} from '../../api';
import InfoIcon from '../shared/info-icon';
import ATCButtonTemplate from '../atc/atc-button-template';
import {modal, Portal} from '../../../util';
import {isEPCustomLike} from '../../rendering/render.def';
import {isEPContentClippedForSize} from '../engraved-plates/ep-clip';
import {EPApprovalPreview, getEffectiveEPSizeForDoc} from '../engraved-plates/ep-editor';
import {getCafDocSizeSubmitAction, validateCafDocSizes} from './caf-doc-size-validation';

declare const collapse: {
    init: () => void;
};

@observer
export default class CAFDocSize extends React.Component {
    @observable doc1_w: number = -1;
    @observable doc1_h: number = -1;
    @observable doc2_w: number = -1;
    @observable doc2_h: number = -1;
    @observable print1_w: number = -1;
    @observable print1_h: number = -1;
    @observable print2_w: number = -1;
    @observable print2_h: number = -1;

    @observable isHorizontal: string = '1';
    @observable notes: string = '';
    @observable debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    @observable error1: string = '';
    @observable error2: string = '';
    @observable error3: string = '';
    @observable error4: string = '';
    @observable showApprovalModal: boolean = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        if (s.ps.docWidth > 0) this.doc1_w = s.ps.docWidth;
        if (s.ps.docHeight > 0) this.doc1_h = s.ps.docHeight;

        if (s.ps.is_horizontal) {
            this.isHorizontal = '1';
        } else {
            this.isHorizontal = '0';
        }

        // The (sticky) Add to Cart button can be clicked from anywhere down the
        // page; showing this panel replaces the much taller customization panel,
        // so the stale scroll offset strands the user below the form (especially
        // on mobile). Bring the CAF sizing panel clearly into view.
        const d = document.getElementById('caf-atc-panel') as HTMLDivElement;
        if (d) {
            d.scrollIntoView();
        }
    }

    componentWillUnmount() {
        this.clearTimeout();
        // The mounted EP editor recomputes this for itself once we return to the
        // designer; clear it so a stale CAF-step warning never lingers.
        runInAction(() => {
            s.ui.ep_overflowing = false;
        });
    }

    @action
    stop_caf = async () => {
        s.ui.show_caf_doc_sizes = false;
        s.ui.show_caf_doc_sizes_both = false;
        s.ui.doRender = true;
        s.pulse();
        setTimeout(function () {
            collapse.init();
        }, 150);
    };

    @action
    setOrientation = (value: string) => {
        this.isHorizontal = value;
        s.ps.is_horizontal = value === '1';
        s.prod.IsHorizontal = s.ps.is_horizontal;
        s.prod.OrientationHorizontal = s.ps.is_horizontal;
        this.renderFrame();
    };

    @action
    atc = async (renderFrame: boolean = false) => {
        const validated = validateCafDocSizes({
            docType: s.prod.DocType,
            dyfFrameStyleId: s.prod.DYF_FrameStyleID,
            showBothDocSizes: s.ui.show_caf_doc_sizes_both,
            doc1_w: this.doc1_w,
            doc1_h: this.doc1_h,
            doc2_w: this.doc2_w,
            doc2_h: this.doc2_h,
            print1_w: this.print1_w,
            print1_h: this.print1_h,
            print2_w: this.print2_w,
            print2_h: this.print2_h,
            epMethodId: s.pcs.EPMethodID,
            hasEngravedPlate: s.pcs.HasEngravedPlate,
            epWidth: s.ps.EPWidth,
            isEpContentClippedForDoc: (docW, docH) => {
                const eff = getEffectiveEPSizeForDoc(docW, docH);
                return isEPContentClippedForSize(s.ps.EPContent, eff.width, eff.height);
            },
        });

        this.error1 = validated.error1;
        this.error2 = validated.error2;
        this.error3 = validated.error3;
        this.error4 = validated.error4;
        s.ui.ep_overflowing = validated.epOverflowing;

        if (validated.hasFieldErrors) {
            return;
        }

        s.ps.docWidth = this.doc1_w;
        s.ps.docHeight = this.doc1_h;
        s.ps.printWidth = this.print1_w;
        s.ps.printHeight = this.print1_h;

        if (s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both) {
            s.ps.docWidth2 = this.doc2_w;
            s.ps.docHeight2 = this.doc2_h;
            s.ps.printWidth2 = this.print2_w;
            s.ps.printHeight2 = this.print2_h;
        } else if (s.prod.DYF_FrameStyleID === 2) {
            s.ps.docWidth2 = this.doc1_w;
            s.ps.docHeight2 = this.doc1_h;
            s.ps.printWidth2 = this.print1_w;
            s.ps.printHeight2 = this.print1_h;
        }

        s.ps.is_horizontal = this.isHorizontal === '1';
        s.prod.IsHorizontal = s.ps.is_horizontal;
        s.prod.OrientationHorizontal = s.ps.is_horizontal;
        s.ps.caf_note_IsHorizontal = s.ps.is_horizontal;
        if (this.notes && this.notes.length > 2) {
            s.ps.caf_note = this.notes.truncate(400, true);
        }

        if (renderFrame) {
            // Keep re-rendering the frame preview as the user types so they can see
            // the (clamped) plate, but the overflow warning below blocks checkout.
            this.renderFrame();
            return;
        }

        const action = getCafDocSizeSubmitAction({
            epOverflowing: s.ui.ep_overflowing,
            atcBusy: s.ui.atc_busy,
            needsApproval: isEPCustomLike(s.pcs.EPMethodID) && s.pcs.HasEngravedPlate,
        });

        if (action === 'blocked_overflow' || action === 'busy') {
            return;
        }
        if (action === 'approval') {
            this.showApprovalModal = true;
            return;
        }

        try {
            await add_to_cart();
        } catch (err) {
            logger.error(err);
        }
    };

    @action
    renderFrame() {
        this.clearTimeout();
        this.debounceTimeout = setTimeout(() => {
            runInAction(() => {
                if (!s.ps.docWidth || !s.ps.docHeight) {
                    return;
                }

                s.ui.doRender = true;
                s.ui.update_pricing = true;
                s.pulse();
            });
        }, 1000);
    }

    @action
    clearTimeout() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
        }
    }

    @action
    approveAndAddToCart = async () => {
        if (s.ui.atc_busy) {
            return;
        }

        this.showApprovalModal = false;

        try {
            await add_to_cart();
        } catch (err) {
            logger.error(err);
        }
    };

    render() {
        return (
            <div
                id="caf-atc-panel"
                className="caf-atc card-m"
            >
                <div
                    aria-live="assertive"
                    aria-atomic="true"
                    className="sr-only"
                >
                    {s.ui.atc_busy ? 'Adding to cart, please wait...' : ''}
                </div>
                <div>Add To Cart</div>

                <div className="text-left">
                    <ModalSimple
                        id="modal-caf-atc-1"
                        title={'Measurement Table'}
                    >
                        <div>
                            <p>Use the table below to convert from fractions to decimal:</p>
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/dyf_help_decimals.webp`}
                                />
                                <img
                                    alt="Measurement table"
                                    loading="lazy"
                                    src={`https://${Portal?.CDNDomain}/images/dyf_help_decimals.png`}
                                />
                            </picture>
                        </div>
                    </ModalSimple>
                    <ModalSimple
                        id="modal-caf-atc-2"
                        title={'How to measure your Document'}
                    >
                        <div>
                            <p>How to measure your Document</p>
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.webp`}
                                />
                                <img
                                    alt="Measurement diagram"
                                    loading="lazy"
                                    src={`https://${Portal?.CDNDomain}/images/cached/general/how_to_measure_document.png`}
                                    className="resp"
                                />
                            </picture>
                        </div>
                    </ModalSimple>

                    <div className="text-left">
                        <p className="mt-2">
                            We require the actual paper size measurements to confirm price and accurately size your frame. Please convert fractions to decimals.{' '}
                            <InfoIcon
                                id="modal-caf-atc-1"
                                ariaLabel="View a photo of an inches to decimals sizing chart"
                            />
                        </p>
                        <p className="mt-3 text-center">
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                onClick={() => modal('modal-caf-atc-2')}
                                aria-haspopup="dialog"
                                aria-label="How to measure your Document"
                            >
                                How to measure your {s.prod.DocType}
                                <span style={{paddingLeft: '1rem'}}>
                                    <i
                                        className="far fa-info-circle"
                                        aria-hidden={true}
                                    />
                                </span>
                            </button>
                        </p>
                        <hr className="mt-4" />

                        <div className="text-left">
                            {s.prod.DYF_FrameStyleID === 2 && !s.ui.show_caf_doc_sizes_both && <div className="subh">Top &amp; Bottom {s.prod.DocType}s</div>}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <div className="subh">Top {s.prod.DocType}</div>}
                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    1. Actual {s.prod.DocType} Size*:{' '}
                                </div>
                                <div
                                    className="input-group input-group-sm"
                                    style={{marginLeft: 15}}
                                >
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="doc1_w"
                                            defaultValue={s.ps.docWidth > 0 ? s.ps.docWidth : undefined}
                                            className="form-control"
                                            placeholder="Width"
                                            style={{width: 125}}
                                            onChange={e => {
                                                this.doc1_w = parseFloat(e.target.value);
                                                this.atc(true);
                                            }}
                                            aria-label="Top Document Width in Inches"
                                        />
                                        <label htmlFor="doc1_w">Width</label>
                                    </div>
                                    <span className="input-group-text">x</span>
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="doc1_h"
                                            defaultValue={s.ps.docHeight > 0 ? s.ps.docHeight : undefined}
                                            className="form-control"
                                            placeholder="Height"
                                            style={{width: 125}}
                                            onChange={e => {
                                                this.doc1_h = parseFloat(e.target.value);
                                                this.atc(true);
                                            }}
                                            aria-label="Top Document Height in Inches"
                                        />
                                        <label htmlFor="doc1_h">Height</label>
                                    </div>
                                    <span className="input-group-text">inches</span>
                                </div>
                                <div
                                    className="hint"
                                    style={{marginLeft: 15}}
                                >
                                    e.g. 10.5" W x 8" H
                                </div>
                                {this.error1 && this.error1 !== '' && <div className="err">{this.error1}</div>}
                            </div>
                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    2. Printed Area Size (optional):{' '}
                                </div>
                                <div
                                    className="input-group input-group-sm"
                                    style={{marginLeft: 15}}
                                >
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="print1_w"
                                            className="form-control"
                                            placeholder="Width"
                                            style={{width: 125}}
                                            onChange={e => (this.print1_w = parseFloat(e.target.value))}
                                            aria-label="Top Document Width in Inches"
                                        />
                                        <label htmlFor="print1_w">Width</label>
                                    </div>
                                    <span className="input-group-text">x</span>
                                    <div
                                        className="form-floating"
                                        style={{width: 125, flex: 'inherit'}}
                                    >
                                        <input
                                            type="number"
                                            step="0.125"
                                            name="print1_h"
                                            className="form-control"
                                            placeholder="Height"
                                            style={{width: 125}}
                                            onChange={e => (this.print1_h = parseFloat(e.target.value))}
                                            aria-label="Top Document Height in Inches"
                                        />
                                        <label htmlFor="print1_h">Height</label>
                                    </div>
                                    <span className="input-group-text">inches</span>
                                </div>
                                <div
                                    className="hint"
                                    style={{marginLeft: 15}}
                                >
                                    e.g. 10.5" W x 8" H
                                </div>
                                {this.error2 && this.error2 !== '' && <div className="err">{this.error2}</div>}
                            </div>

                            {s.prod.DYF_FrameStyleID === 2 && !s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <p>Does your bottom {s.prod.DocType.toLowerCase()} have different dimensions?</p>
                                    <div style={{marginLeft: 15}}>
                                        <button
                                            type="button"
                                            onClick={() => (s.ui.show_caf_doc_sizes_both = true)}
                                            className="btn btn-outline-dark btn-sm"
                                        >
                                            Yes
                                        </button>
                                    </div>
                                </div>
                            )}

                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <div className="subh mt-4">Bottom {s.prod.DocType}</div>}
                            {/* {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && <p className='hint' style={{marginLeft:15}}>Bottom {s.prod.DocType} information may be left blank if both {s.prod.DocType}s have identical dimensions.</p>} */}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <div
                                        className="header-std"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        1. Actual {s.prod.DocType} Size*:{' '}
                                    </div>
                                    <div
                                        className="input-group input-group-sm"
                                        style={{marginLeft: 15}}
                                    >
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="doc2_w"
                                                className="form-control"
                                                placeholder="Width"
                                                style={{width: 125}}
                                                onChange={e => (this.doc2_w = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Width in Inches"
                                            />
                                            <label htmlFor="doc2_w">Width</label>
                                        </div>
                                        <span className="input-group-text">x</span>
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="doc2_h"
                                                className="form-control"
                                                placeholder="Height"
                                                style={{width: 125}}
                                                onChange={e => (this.doc2_h = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Height in Inches"
                                            />
                                            <label htmlFor="doc2_h">Height</label>
                                        </div>
                                        <span className="input-group-text">inches</span>
                                    </div>
                                    {this.error3 && this.error3 !== '' && <div className="err">{this.error3}</div>}
                                </div>
                            )}
                            {s.prod.DYF_FrameStyleID === 2 && s.ui.show_caf_doc_sizes_both && (
                                <div className="mt-3">
                                    <div
                                        className="header-std"
                                        style={{paddingRight: '0.25rem'}}
                                    >
                                        2. Printed Area Size (optional):{' '}
                                    </div>
                                    <div
                                        className="input-group input-group-sm"
                                        style={{marginLeft: 15}}
                                    >
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="print2_w"
                                                className="form-control"
                                                placeholder="Width"
                                                style={{width: 125}}
                                                onChange={e => (this.print2_w = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Width in Inches"
                                            />
                                            <label htmlFor="print2_w">Width</label>
                                        </div>
                                        <span className="input-group-text">x</span>
                                        <div
                                            className="form-floating"
                                            style={{width: 125, flex: 'inherit'}}
                                        >
                                            <input
                                                type="number"
                                                name="print2_h"
                                                className="form-control"
                                                placeholder="Height"
                                                style={{width: 125}}
                                                onChange={e => (this.print2_h = parseFloat(e.target.value))}
                                                step="0.125"
                                                maxLength={7}
                                                aria-label="Bottom Document Height in Inches"
                                            />
                                            <label htmlFor="print2_h">Height</label>
                                        </div>
                                        <span className="input-group-text">inches</span>
                                    </div>
                                    {this.error4 && this.error4 !== '' && <div className="err">{this.error4}</div>}
                                </div>
                            )}

                            <hr />

                            <div className="mt-3">
                                <div
                                    className="header-std"
                                    style={{paddingRight: '0.25rem'}}
                                >
                                    {s.prod.DocType} Orientation &amp; Notes:{' '}
                                </div>
                                <select
                                    className="form-select"
                                    aria-label="Select document orientation"
                                    value={this.isHorizontal}
                                    onChange={e => this.setOrientation(e.target.value)}
                                >
                                    <option value="1">Horizontal / Landscape</option>
                                    <option value="0">Vertical / Portrait</option>
                                </select>
                            </div>
                            <div className="mt-2 d-none">
                                <div
                                    className="form-floating"
                                    style={{}}
                                >
                                    <textarea
                                        name="caf_notes"
                                        maxLength={400}
                                        style={{height: 65}}
                                        onChange={e => (this.notes = e.target.value)}
                                        className="form-control"
                                        placeholder="Notes"
                                        aria-label="Notes for your order"
                                    />
                                    <label htmlFor="caf_notes">Notes For Us</label>
                                </div>
                            </div>
                            <hr />
                            <div className="text-center mb-2">
                                {s.ui.ep_overflowing && <div className="err mb-4">Text is being cut off. Reduce text or font size to fit within the plate.</div>}
                                {s.ps && s.ps.atc_error && s.ps.atc_error !== '' && (
                                    <div
                                        className="err mb-4"
                                        role="alert"
                                        aria-live="assertive"
                                    >
                                        {s.ps.atc_error}
                                    </div>
                                )}
                                <div
                                    className="btn-group"
                                    role="group"
                                >
                                    <button
                                        type="button"
                                        className="btn btn-outline-dark"
                                        aria-label="Back to frame designer"
                                        onClick={this.stop_caf}
                                    >
                                        Back to Design
                                    </button>
                                    <ATCButtonTemplate
                                        clicked={() => this.atc()}
                                        additionalClass="btn"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {this.showApprovalModal &&
                    createPortal(
                        <>
                            <div className="modal-backdrop show" />
                            <div
                                className="modal d-block"
                                tabIndex={-1}
                                role="dialog"
                                aria-modal="true"
                                aria-labelledby="caf-approval-modal-title"
                                onClick={e => {
                                    if ((e.target as HTMLElement).classList.contains('modal'))
                                        runInAction(() => {
                                            this.showApprovalModal = false;
                                        });
                                }}
                            >
                                <div
                                    className="modal-dialog modal-dialog-centered"
                                    style={{maxWidth: 700}}
                                >
                                    <div className="modal-content">
                                        <div
                                            className="modal-header"
                                            role="heading"
                                            aria-level={1}
                                        >
                                            <span id="caf-approval-modal-title">Customization Approval</span>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                aria-label="Close"
                                                onClick={() =>
                                                    runInAction(() => {
                                                        this.showApprovalModal = false;
                                                    })
                                                }
                                            />
                                        </div>
                                        <div className="modal-body text-center">
                                            <p style={{marginBottom: 16}}>Please confirm that the custom plate proof is what you want.</p>
                                            <EPApprovalPreview />
                                        </div>
                                        <div
                                            className="modal-footer"
                                            style={{justifyContent: 'center', gap: 8}}
                                        >
                                            <button
                                                type="button"
                                                className="btn btn-outline-dark"
                                                onClick={() =>
                                                    runInAction(() => {
                                                        this.showApprovalModal = false;
                                                    })
                                                }
                                            >
                                                Continue Editing
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={this.approveAndAddToCart}
                                                disabled={s.ui.atc_busy || s.ui.ep_overflowing}
                                                aria-busy={s.ui.atc_busy}
                                            >
                                                Approved
                                                {s.ui.atc_busy && (
                                                    <span
                                                        className="pad-left"
                                                        aria-hidden="true"
                                                    >
                                                        <i
                                                            className="fas fa-circle-notch fa-spin fast-spin fa-checkout-button"
                                                            aria-hidden={true}
                                                        />
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>,
                        document.body,
                    )}
            </div>
        );
    }
}
